import React from 'react'
import PropTypes from 'prop-types'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import './Sparkline.scss'

/**
 * Sparkline - Minimal line chart for inline metrics
 *
 * @param {Array} data - Data array [{value: number}]
 * @param {string} dataKey - Key for data values
 * @param {number} width - Chart width
 * @param {number} height - Chart height
 * @param {string} color - Line/fill color
 * @param {boolean} showArea - Show area fill under line
 * @param {number} strokeWidth - Line stroke width
 * @param {string} variant - Color variant ('primary' | 'success' | 'warning' | 'danger')
 * @param {boolean} loading - Show loading state
 */
const Sparkline = ({
	data = [],
	dataKey = 'value',
	width = '100%',
	height = 40,
	color,
	showArea = true,
	strokeWidth = 2,
	variant = 'primary',
	loading = false
}) => {
	const getVariantColor = () => {
		if (color) return color

		const colors = {
			primary: '#3b82f6',
			success: '#10b981',
			warning: '#f59e0b',
			danger: '#ef4444',
			info: '#6366f1'
		}
		return colors[variant] || colors.primary
	}

	const lineColor = getVariantColor()
	const gradientId = `sparkline-gradient-${variant}-${Math.random().toString(36).substr(2, 9)}`

	if (loading) {
		return (
			<div className="sparkline sparkline--loading" style={{ width, height }}>
				<div className="skeleton skeleton--line"></div>
			</div>
		)
	}

	if (data.length === 0) {
		return (
			<div className="sparkline sparkline--empty" style={{ width, height }}>
				<span>—</span>
			</div>
		)
	}

	return (
		<div className="sparkline" style={{ width, height }}>
			<ResponsiveContainer width="100%" height={height}>
				<AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
					<defs>
						<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={lineColor} stopOpacity={0.3} />
							<stop offset="100%" stopColor={lineColor} stopOpacity={0} />
						</linearGradient>
					</defs>
					<Area
						type="monotone"
						dataKey={dataKey}
						stroke={lineColor}
						strokeWidth={strokeWidth}
						fill={showArea ? `url(#${gradientId})` : 'transparent'}
						dot={false}
						activeDot={false}
						isAnimationActive={true}
						animationDuration={500}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	)
}

Sparkline.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object),
	dataKey: PropTypes.string,
	width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	height: PropTypes.number,
	color: PropTypes.string,
	showArea: PropTypes.bool,
	strokeWidth: PropTypes.number,
	variant: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
	loading: PropTypes.bool
}

export default Sparkline
